/* eslint-disable @typescript-eslint/no-explicit-any */
import Button from '@brands/Components/Button/Button';
import MuiLongTextField from '@brands/Components/Inputs/MuiLongTextField/MuiLongTextField';
import MultiSelectInputMUI from '@brands/Components/Inputs/MuiMultiSelectInput/MultiSelectInputMUI';
import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import PhoneInput from '@brands/Components/Inputs/MuiTelInput/MuiTelInput';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import Sidenav from '@brands/Dashboard/Sidenav/Sidenav';
import { HashedUser } from '@brands/services/auth/getUserByHash';
import { listAllLanguages } from '@brands/services/identity/listAllLanguages';
import { genderIdentity, genderOptions, pronounsOptions } from '@brands/Utils/selectButtonOptions';
import { Option, OptionLanguage, specialtyOptions, stateOptions } from '@brands/Utils/selectOptions';
import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete, Chip } from '@mui/material';
import { ArrowDropDownIcon, ClearIcon } from '@mui/x-date-pickers';
import { E164Number } from 'libphonenumber-js';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { displayErrorDetails } from '../../../Utils/displayError';
import styles from '../styles.module.scss';
import { completeProfileValidationSchema, UserCompleteProfileForm } from '../types';
import rstyles from './roleForm.module.scss';
import { getRoleFormConfig } from './roleFormConfig';

interface Props {
  signUp: (data: UserCompleteProfileForm) => Promise<void>;
  invitedUser: HashedUser | null;
}

type SpecialtyOption = { label: string; value: string };

const RoleCompleteProfileForm: FC<Props> = ({ signUp, invitedUser }) => {
  const role = invitedUser?.role?.name;
  const config = getRoleFormConfig(role);

  const [loading, setLoading] = useState(false);
  const [languageOptions, setLanguageOptions] = useState<OptionLanguage[]>([]);
  const [educationInputValue, setEducationInputValue] = useState('');
  const [specialtyInputValue, setSpecialtyInputValue] = useState('');
  const phoneInputRef = useRef<HTMLInputElement>(null);

  const fetchLanguageOptions = async (): Promise<void> => {
    const data = await listAllLanguages();
    setLanguageOptions(
      data.map((language) => ({
        value: language.locale,
        label: language.name,
        id: language.id,
      })),
    );
  };

  useEffect(() => {
    if (config.showLanguages) {
      fetchLanguageOptions().catch(displayErrorDetails);
    }
  }, [config.showLanguages]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
    clearErrors,
  } = useForm<UserCompleteProfileForm>({
    mode: 'onChange',
    resolver: yupResolver(completeProfileValidationSchema(role)),
    defaultValues: {
      first_name: invitedUser?.basic_info?.first_name || '',
      middle_name: invitedUser?.basic_info?.middle_name || '',
      last_name: invitedUser?.basic_info?.last_name || '',
      pronouns: invitedUser?.basic_info?.pronouns || '',
      gender: invitedUser?.basic_info?.gender || '',
      gender_identity: invitedUser?.basic_info?.gender_identity || '',
      language: invitedUser?.basic_info?.languages?.map((lang) => lang.id?.toString()) || [],
      phone_number: invitedUser?.phone_number || '',
      address1: invitedUser?.address?.address1 || '',
      address2: invitedUser?.address?.address2 || '',
      city: invitedUser?.address?.city || '',
      state: invitedUser?.address?.state || '',
      zip_code: invitedUser?.address?.zip_code || '',
      education: [],
      specialty: [],
      additional_credentials: '',
      biography: invitedUser?.care_advisor_profile?.biography || '',
    },
  });

  const onInvalid = (err: any): void => {
    const error = Object.values(err)[0] as any;
    if (error) {
      displayErrorDetails(error);
    }
  };

  const onSubmit = async (data: UserCompleteProfileForm): Promise<void> => {
    setLoading(true);
    try {
      await signUp(data);
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
    setLoading(false);
  };

  const education = (watch('education') as string[]) || [];
  const specialty = (watch('specialty') as SpecialtyOption[]) || [];
  const organizationName = invitedUser?.organizations?.[0]?.name || '';
  const readOnly = config.readOnly;
  const nameFieldsReadOnly = readOnly || config.lockNameFields;

  const inputClass = `${styles.profileInputClass} fs-exclude`;
  const wrapper = (extra?: string): string =>
    `fs-unmask ${extra ? `${extra} ` : ''}${styles.completeProfileClass} ${rstyles.fieldWrapper}`;

  return (
    <>
      <Sidenav isVisibleOnMobile hideDefaultOptions authLoog />
      <div className={styles.body}>
        <div className={styles.leftSection}>
          <div className={styles.container}>
            <form className={rstyles.roleFormContainer} onSubmit={handleSubmit(onSubmit, onInvalid)}>
              <span className={styles.formTitle}>
                <span className={`${styles.formTitleWelcome} fs-unmask`} data-testid="complete-profile-title">
                  {readOnly ? 'Confirm your profile' : 'Complete your profile'}
                </span>
                <span className={`fs-unmask ${styles.formTitleWelcomeSub}`}>
                  {readOnly
                    ? 'Please review your information below and confirm to continue'
                    : 'We need a bit more information to finish creating your account'}
                </span>
              </span>

              {/* Name */}
              <div className={rstyles.row}>
                <div className={rstyles.field}>
                  <TextField
                    {...register('first_name')}
                    data-testid="complete-profile-first_name"
                    label="First name"
                    errors={errors.first_name ? errors : {}}
                    name="first_name"
                    value={nameFieldsReadOnly ? watch('first_name') : undefined}
                    onChange={(e: any) => {
                      if (errors.first_name) clearErrors('first_name');
                      setValue('first_name', e.target.value);
                    }}
                    required={!nameFieldsReadOnly}
                    disabled={nameFieldsReadOnly}
                    inputClass={inputClass}
                    wrapperClassName={wrapper()}
                  />
                </div>
                {config.showMiddleName && (
                  <div className={rstyles.field}>
                    <TextField
                      {...register('middle_name')}
                      data-testid="complete-profile-middle_name"
                      label="Middle name (optional)"
                      errors={errors.middle_name ? errors : {}}
                      name="middle_name"
                      value={nameFieldsReadOnly ? watch('middle_name') : undefined}
                      onChange={(e: any) => {
                        if (errors.middle_name) clearErrors('middle_name');
                        setValue('middle_name', e.target.value);
                      }}
                      disabled={nameFieldsReadOnly}
                      inputClass={inputClass}
                      wrapperClassName={wrapper()}
                    />
                  </div>
                )}
                <div className={rstyles.field}>
                  <TextField
                    {...register('last_name')}
                    data-testid="complete-profile-last_name"
                    label="Last name"
                    errors={errors.last_name ? errors : {}}
                    name="last_name"
                    value={nameFieldsReadOnly ? watch('last_name') : undefined}
                    onChange={(e: any) => {
                      if (errors.last_name) clearErrors('last_name');
                      setValue('last_name', e.target.value);
                    }}
                    required={!nameFieldsReadOnly}
                    disabled={nameFieldsReadOnly}
                    inputClass={inputClass}
                    wrapperClassName={wrapper()}
                  />
                </div>
              </div>

              {/* Email (always read-only at first launch) + phone */}
              <div className={rstyles.row}>
                {config.showEmailDisplay && (
                  <div className={rstyles.field}>
                    <TextField
                      data-testid="complete-profile-email"
                      label="Email address"
                      name="email_display"
                      value={invitedUser?.email || ''}
                      disabled
                      inputClass={inputClass}
                      wrapperClassName={wrapper()}
                    />
                  </div>
                )}
                <div className={rstyles.field}>
                  <PhoneInput
                    {...register('phone_number')}
                    data-testid="complete-profile-phone_number"
                    value={watch('phone_number') as E164Number}
                    id="phone_number"
                    name="phone_number"
                    onChange={(val: string | undefined) => {
                      setValue('phone_number', val === undefined ? '' : val, { shouldValidate: true });
                    }}
                    required={!readOnly}
                    disabled={readOnly}
                    label="Phone number"
                    errors={errors}
                    inputClass={inputClass}
                    wrapperClassName={wrapper(styles.completeProfilePhone)}
                    ref={phoneInputRef}
                  />
                </div>
              </div>

              {/* Organization (read-only display) */}
              {config.showOrganization && (
                <div className={rstyles.row}>
                  <div className={rstyles.field}>
                    <TextField
                      data-testid="complete-profile-organization"
                      label="Organization"
                      name="organization_display"
                      value={organizationName}
                      disabled
                      inputClass={inputClass}
                      wrapperClassName={wrapper()}
                    />
                  </div>
                </div>
              )}

              {/* Gender block */}
              {config.showGenderBlock && (
                <div className={rstyles.row}>
                  <div className={rstyles.field}>
                    <MUISelectInput
                      {...register('gender')}
                      data-testid="complete-profile-gender"
                      label="Gender assigned at birth"
                      errors={errors.gender ? errors : {}}
                      value={getValues('gender') || ''}
                      name="gender"
                      onChange={(e: any) => {
                        if (errors.gender) clearErrors('gender');
                        setValue('gender', e, { shouldValidate: true });
                      }}
                      required
                      inputClass={inputClass}
                      wrapperClassName={wrapper(styles.completeProfileClassGender)}
                      options={genderOptions}
                    />
                  </div>
                  <div className={rstyles.field}>
                    <MUISelectInput
                      {...register('gender_identity')}
                      data-testid="complete-profile-gender-identity"
                      label="Gender identity"
                      value={watch('gender_identity') || ''}
                      errors={errors.gender_identity ? errors : {}}
                      name="gender_identity"
                      onChange={(e: any) => {
                        if (errors.gender_identity) clearErrors('gender_identity');
                        setValue('gender_identity', e);
                      }}
                      required
                      inputClass={inputClass}
                      wrapperClassName={wrapper(styles.completeProfileClassIdentity)}
                      options={genderIdentity}
                    />
                  </div>
                  <div className={rstyles.field}>
                    <MUISelectInput
                      {...register('pronouns')}
                      data-testid="complete-profile-pronouns"
                      label="Preferred pronouns (optional)"
                      errors={errors.pronouns ? errors : {}}
                      value={watch('pronouns') || ''}
                      name="pronouns"
                      onChange={(e: any) => {
                        if (errors.pronouns) clearErrors('pronouns');
                        setValue('pronouns', e);
                      }}
                      inputClass={inputClass}
                      wrapperClassName={wrapper(styles.completeProfileClassPronouns)}
                      options={pronounsOptions}
                    />
                  </div>
                </div>
              )}

              {/* Languages */}
              {config.showLanguages && (
                <div className={rstyles.row}>
                  <div className={rstyles.field}>
                    <MultiSelectInputMUI
                      {...register('language')}
                      data-testid="complete-profile-language"
                      label="Languages"
                      errors={errors.language ? errors : {}}
                      name="language"
                      onChange={(selectedValues: any) => {
                        if (errors.language) clearErrors('language');
                        setValue('language', selectedValues, { shouldValidate: true });
                      }}
                      withId
                      value={
                        Array.isArray(watch('language')) && typeof watch('language')[0] === 'string'
                          ? (watch('language') as string[])
                          : Array.isArray(watch('language')) && typeof watch('language')[0] === 'object'
                          ? ((watch('language') as OptionLanguage[]).map((lang: OptionLanguage) =>
                              lang.id?.toString(),
                            ) as string[])
                          : []
                      }
                      required
                      inputClass={inputClass}
                      wrapperClassName={wrapper(styles.completeProfileLanguage)}
                      options={languageOptions}
                    />
                  </div>
                </div>
              )}

              {/* Address */}
              {config.addressMode === 'full' && (
                <>
                  <div className={rstyles.row}>
                    <div className={rstyles.field}>
                      <TextField
                        {...register('address1')}
                        data-testid="complete-profile-address1"
                        label="Address"
                        errors={errors.address1 ? errors : {}}
                        name="address1"
                        onChange={(e: any) => {
                          if (errors.address1) clearErrors('address1');
                          setValue('address1', e.target.value);
                        }}
                        required
                        inputClass={inputClass}
                        wrapperClassName={wrapper(styles.completeProfileAddress)}
                      />
                    </div>
                    <div className={rstyles.field}>
                      <TextField
                        {...register('address2')}
                        data-testid="complete-profile-address2"
                        label="Apartment, suite, etc. (optional)"
                        errors={errors.address2 ? errors : {}}
                        name="address2"
                        onChange={(e: any) => {
                          if (errors.address2) clearErrors('address2');
                          setValue('address2', e.target.value);
                        }}
                        inputClass={inputClass}
                        wrapperClassName={wrapper(styles.completeProfileAddress2)}
                      />
                    </div>
                  </div>
                  <div className={rstyles.row}>
                    <div className={rstyles.field}>
                      <TextField
                        {...register('city')}
                        data-testid="complete-profile-city"
                        label="City"
                        errors={errors.city ? errors : {}}
                        name="city"
                        onChange={(e: any) => {
                          if (errors.city) clearErrors('city');
                          setValue('city', e.target.value);
                        }}
                        required
                        inputClass={inputClass}
                        wrapperClassName={wrapper()}
                      />
                    </div>
                    <div className={rstyles.field}>
                      <MUISelectInput
                        {...register('state')}
                        data-testid="complete-profile-state"
                        label="State"
                        errors={errors.state ? errors : {}}
                        name="state"
                        onChange={(e: any) => {
                          if (errors.state) clearErrors('state');
                          setValue('state', e);
                        }}
                        value={watch('state') || ''}
                        required
                        inputClass={inputClass}
                        wrapperClassName={wrapper()}
                        options={stateOptions}
                      />
                    </div>
                  </div>
                </>
              )}
              {(config.addressMode === 'full' || config.addressMode === 'zip') && (
                <div className={rstyles.row}>
                  <div className={rstyles.field}>
                    <TextField
                      {...register('zip_code')}
                      data-testid="complete-profile-zip-code"
                      label="Zip code"
                      errors={errors.zip_code ? errors : {}}
                      name="zip_code"
                      onChange={(e: any) => {
                        if (errors.zip_code) clearErrors('zip_code');
                        setValue('zip_code', e.target.value, { shouldValidate: true });
                      }}
                      required
                      inputClass={inputClass}
                      wrapperClassName={wrapper()}
                    />
                  </div>
                </div>
              )}

              {/* Provider professional fields */}
              {config.showProviderFields && (
                <>
                  <div className={rstyles.row}>
                    <div className={rstyles.field}>
                      <Autocomplete<string, true, false, true>
                        multiple
                        freeSolo
                        fullWidth
                        options={[]}
                        value={education}
                        inputValue={educationInputValue}
                        onInputChange={(_, v) => setEducationInputValue(v)}
                        onChange={(_, value) => setValue('education', value, { shouldValidate: true })}
                        popupIcon={<ArrowDropDownIcon />}
                        clearIcon={<ClearIcon />}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option}
                              label={option}
                              sx={{
                                height: 28,
                                borderRadius: '16px',
                                backgroundColor: '#EEF5FF',
                                color: 'var(--primary-color)',
                                border: '1px solid var(--primary-color)',
                                '& .MuiChip-deleteIcon': { color: 'var(--primary-color)' },
                              }}
                            />
                          ))
                        }
                        renderInput={(params) => {
                          const { InputProps, inputProps } = params;
                          return (
                            <TextField
                              name="education"
                              label="Education / Universities"
                              required
                              placeholder="Type and press Enter"
                              errors={errors.education ? errors : {}}
                              wrapperClassName={wrapper()}
                              inputClass={styles.profileInputClass}
                              InputProps={InputProps}
                              inputProps={inputProps}
                            />
                          );
                        }}
                      />
                    </div>
                  </div>

                  <div className={rstyles.row}>
                    <div className={rstyles.field}>
                      <Autocomplete<SpecialtyOption, true, false, true>
                        multiple
                        freeSolo
                        fullWidth
                        options={specialtyOptions as SpecialtyOption[]}
                        value={specialty}
                        inputValue={specialtyInputValue}
                        onInputChange={(_, v) => setSpecialtyInputValue(v)}
                        onChange={(_, value) => {
                          const normalized = value.map((v) => (typeof v === 'string' ? { label: v, value: v } : v));
                          setValue('specialty', normalized as Option[], { shouldValidate: true });
                        }}
                        getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
                        popupIcon={<ArrowDropDownIcon />}
                        clearIcon={<ClearIcon />}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option.value}
                              label={option.label}
                              sx={{
                                height: 28,
                                borderRadius: '16px',
                                backgroundColor: '#EEF5FF',
                                color: 'var(--primary-color)',
                                border: '1px solid var(--primary-color)',
                                '& .MuiChip-deleteIcon': { color: 'var(--primary-color)' },
                              }}
                            />
                          ))
                        }
                        renderInput={(params) => {
                          const { InputProps, inputProps } = params;
                          return (
                            <TextField
                              name="specialty"
                              label="Specialties (select up to 6)"
                              required
                              placeholder="Type and press Enter"
                              errors={errors.specialty ? errors : {}}
                              wrapperClassName={wrapper()}
                              inputClass={styles.profileInputClass}
                              InputProps={InputProps}
                              inputProps={inputProps}
                            />
                          );
                        }}
                      />
                    </div>
                  </div>

                  <div className={rstyles.row}>
                    <div className={rstyles.field}>
                      <TextField
                        label="Additional credentials (optional)"
                        name="additional_credentials"
                        data-testid="complete-profile-additional_credentials"
                        onChange={(e: any) => {
                          if (errors.additional_credentials) clearErrors('additional_credentials');
                          setValue('additional_credentials', e.target.value, { shouldValidate: true });
                        }}
                        errors={errors.additional_credentials ? errors : {}}
                        value={watch('additional_credentials')}
                        multiline
                        wrapperClassName={wrapper()}
                        inputClass={styles.profileInputClass}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Biography (Provider + CareAdvisor) */}
              {(config.showProviderFields || config.showBiography) && (
                <div className={rstyles.row}>
                  <div className={rstyles.field}>
                    <MuiLongTextField
                      label="Biography"
                      name="biography"
                      data-testid="complete-profile-biography"
                      onChange={(e: any) => {
                        if (errors.biography) clearErrors('biography');
                        setValue('biography', e.target.value, { shouldValidate: true });
                      }}
                      errors={errors.biography ? errors : {}}
                      value={watch('biography')}
                      multiline
                      required
                      wrapperClassName={wrapper()}
                      inputClass={styles.profileInputClass}
                    />
                  </div>
                </div>
              )}

              <div className={styles.buttonDiv}>
                <Button
                  type="submit"
                  data-testid="complete-profile-save-profile"
                  disabled={loading}
                  className={`${loading && styles.inActiveBtn} fs-unmask ${styles.formBtn}`}
                  style={{ borderRadius: '32px', height: '42px', width: 'auto' }}
                >
                  {config.submitLabel}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default RoleCompleteProfileForm;
